import React, { useEffect, useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Avatar, 
  Button, 
  Divider,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  Home,
  Add,
  Favorite,
  Receipt,
  Settings,
  Logout
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase'; // Make sure to import supabase

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    properties: 0,
    favorites: 0,
    inquiries: 0
  });

  const userData = user?.user_metadata || {};
  
  useEffect(() => {
    const fetchUserStats = async () => {
      try {
        setLoading(true);
        
        console.log('=== DASHBOARD DEBUG START ===');
        console.log('1. Supabase auth user:', user);
        console.log('2. User email:', user?.email);
        console.log('3. User ID (UUID):', user?.id);
        
        // First, find the user in our custom database using their email
        const { data: customUser, error: customUserError } = await supabase
          .from('users')
          .select('id, email')
          .eq('email', user?.email)
          .single();
          
        if (customUserError) {
          console.error('Error finding user in custom database:', customUserError);
          
          // Let's see what users exist
          const { data: allUsers, error: allUsersError } = await supabase
            .from('users')
            .select('id, email')
            .limit(10);
            
          console.log('4. All users in database:', allUsers);
          throw new Error(`User not found in database. Email: ${user?.email}`);
        }
        
        console.log('4. Custom user found:', customUser);
        console.log('5. Custom user ID (integer):', customUser.id);
        
        // Now get properties using the integer user ID
        const { data: userProperties, error: propertiesError } = await supabase
          .from('properties')
          .select('*')
          .eq('owner_id', customUser.id);
          
        if (propertiesError) {
          console.error('Error fetching user properties:', propertiesError);
        }
        
        console.log('6. User properties:', userProperties);
        console.log('7. Properties count:', userProperties?.length || 0);
        
        // Also check for properties with UUID owner_id (old properties)
        const { data: uuidProperties, error: uuidPropertiesError } = await supabase
          .from('properties')
          .select('*')
          .eq('owner_id', user?.id);
          
        if (!uuidPropertiesError) {
          console.log('8. UUID-based properties:', uuidProperties);
        }
        
        const totalProperties = (userProperties?.length || 0) + (uuidProperties?.length || 0);
        console.log('9. Total properties (integer + UUID):', totalProperties);
        console.log('=== DASHBOARD DEBUG END ===');
        
        setStats({
          properties: totalProperties,
          favorites: 0, // TODO: Implement favorites system
          inquiries: 0  // TODO: Implement inquiries system
        });
      } catch (err) {
        console.error('Error in fetchUserStats:', err);
        setStats({
          properties: 0,
          favorites: 0,
          inquiries: 0
        });
      } finally {
        setLoading(false);
      }
    };
    
    if (user?.id && user?.email) {
      fetchUserStats();
    }
  }, [user?.id, user?.email]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Welcome back, {userData.first_name || 'User'}!
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Here's what's happening with your account.
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar 
                  src={userData.avatar_url}
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    mr: 3,
                    bgcolor: 'primary.main',
                    fontSize: '2rem',
                    textTransform: 'uppercase'
                  }}
                >
                  {(userData.first_name?.charAt(0) || user?.email?.charAt(0) || 'U')}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {userData.full_name || 'User'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user?.email}
                  </Typography>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    sx={{ mt: 1 }}
                    onClick={() => navigate('/profile')}
                  >
                    Edit Profile
                  </Button>
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <List dense>
                {userData.phone && (
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Phone fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={userData.phone} />
                  </ListItem>
                )}
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Email fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={user?.email} />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Home color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    MY PROPERTIES
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {stats.properties}
                </Typography>
                <Button 
                  size="small" 
                  startIcon={<Add />} 
                  sx={{ mt: 1, mr: 1 }}
                  onClick={() => navigate('/add-property')}
                >
                  Add New
                </Button>
                <Button 
                  size="small" 
                  startIcon={<Settings />} 
                  variant="outlined"
                  sx={{ mt: 1 }}
                  onClick={() => navigate('/edit-properties')}
                >
                  Edit Properties
                </Button>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Favorite color="error" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    FAVORITES
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {stats.favorites}
                </Typography>
                <Button 
                  size="small" 
                  sx={{ mt: 1 }}
                  onClick={() => navigate('/favorites')}
                >
                  View All
                </Button>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Receipt color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    INQUIRIES
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {stats.inquiries}
                </Typography>
                <Button 
                  size="small" 
                  sx={{ mt: 1 }}
                  onClick={() => navigate('/inquiries')}
                >
                  View All
                </Button>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Quick Actions
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Button 
                      fullWidth 
                      variant="outlined" 
                      startIcon={<Settings />}
                      onClick={() => navigate('/settings')}
                    >
                      Account Settings
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Button 
                      fullWidth 
                      variant="outlined" 
                      color="error" 
                      startIcon={<Logout />}
                      onClick={handleLogout}
                    >
                      Logout
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;
